import React, { FC, useMemo, useState } from 'react'
import Grid from '@mui/material/Grid'
import { useTranslation } from 'react-i18next'
import { Alert } from '@carsys/ui'
import { Dropdown, Form } from '@carsys/form'
import { DontKnowLoginDetailsDialog } from '../DontKnowLoginDetailsDialog'
import { UseWholesalerValidatorResult, useWholesalerValidatorBuilder } from 'app/auth/components/hooks/use-wholesaler-validator-builder'
import { Locales } from '@carsys/enums/locales'
import { IShopWholesaler, OrderAccountCredentials, OrderSystem } from '@cbgms/api'
import { BaseStepLayout } from '../../BaseStepLayout'
import { defaultStepFormProps } from '../../types'
import { StepNavigation } from '../../StepNavigation'
import { useSelector } from 'react-redux'
import { IRootReducerState } from 'state/rootReducer'
import { setTempLocale } from 'app/auth/state/actions'
import { enumToLanguageOptions, supportedLanguagesPerEnvironment } from '@cbgms/base/translate/locales'
import { WithRunInBackgroundHelper } from '../../use-should-run-in-background'
import { WholesalerCredentialFields } from 'app/accounts/components/SetupWholesalers/WholesalerCredentialFields'

export const OnboardingCredentialsFormDefault: FC<
  defaultStepFormProps<any> & {
    basePath: string
    onSubmit: (result: UseWholesalerValidatorResult) => void
    wholesaler: IShopWholesaler
  }
> = ({
  onSubmit,
  wholesaler,
  isFirstStep,
  prevStep,
  isPersisting,
  onSubmitSuccess,
  hideSkip,
  shouldRunInBackground,
  duplicationCheck,
  initialData,
  basePath
}) => {
  const { t, i18n } = useTranslation()
  const [initialLocale] = useState(i18n.resolvedLanguage)
  const environment = useSelector((state: IRootReducerState) => state.environment.Environment)
  const supportedLanguagesForCurrentEnvironment = supportedLanguagesPerEnvironment[environment]
  const localeOptions = enumToLanguageOptions(supportedLanguagesForCurrentEnvironment)

  const incorrectCredentialsMessage = t('Error.API.ErrCreateThirdPartyLicenseInvalidCredentials')

  const submitHandler = useWholesalerValidatorBuilder(basePath, i18n.resolvedLanguage || 'en-GB', wholesaler.UUID, duplicationCheck, {
    onSuccess: result => {
      onSubmit(result)
    }
  })

  const setNewLocale = async (locale: Locales) => {
    await i18n.changeLanguage(locale)
    setTempLocale(locale)
  }

  const onLocaleChange = (e?: React.ChangeEvent<any>) => {
    e?.target?.value && setNewLocale(e?.target?.value)
  }

  const initialCredentialValues = useMemo(() => {
    return wholesaler?.UserFields.reduce((prev, curr) => {
      let value = ''
      if (curr.Name === 'DebtorNumber' || curr.Name === 'Username' || curr.Name === 'UserId') {
        value = (initialData as any).Username || ''
      }
      if (curr.Name === 'Password') {
        value = (initialData as any).Password || ''
      }
      if ((wholesaler.OrderSystem === OrderSystem.S1 || wholesaler.OrderSystem === OrderSystem.VPOM_S1) && curr.Name === 'ExternalID') {
        value = (initialData as any).MGID || ''
      }
      return Object.assign(prev, { [curr.Name]: value })
    }, {} as OrderAccountCredentials)
  }, [wholesaler])

  return (
    <BaseStepLayout title={t('Auth.OnboardingTitle')} subTitles={[t('Auth.OnboardingDefaultWholesalerCredentialsMessage')]}>
      <Form
        enableReinitialize
        NavigationConfirmation={null}
        initialValues={{
          ...initialData,
          Credentials: initialCredentialValues,
          Locale: initialData.Locale || initialLocale
        }}
        onSubmit={async values => {
          await submitHandler.mutateAsync(values.Credentials)
          return values
        }}
        onSubmitSuccess={onSubmitSuccess}
      >
        {({ isSubmitting, handleSubmit }) => (
          <WithRunInBackgroundHelper enabled={shouldRunInBackground} isSubmitting={isSubmitting} onSubmit={handleSubmit}>
            <Grid container>
              {submitHandler.isError && (
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <Alert square severity='error' title={t('global.error')}>
                    {incorrectCredentialsMessage}
                  </Alert>
                </Grid>
              )}
              <Grid item container spacing={2} xs={12} lg={6}>
                <Grid item xs={12}>
                  {localeOptions.length > 1 && (
                    <Dropdown name='Locale' onChange={onLocaleChange} label={t('Auth.SelectLanguage')} options={localeOptions} />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <WholesalerCredentialFields hideDoNotShowOnListOptions hideImage wholesaler={wholesaler} />
                </Grid>
                <Grid item xs={12}>
                  <DontKnowLoginDetailsDialog wholesaler={wholesaler} />
                </Grid>
              </Grid>
            </Grid>
            <StepNavigation
              prevStep={prevStep}
              hideBack={isFirstStep}
              isLoading={submitHandler.isLoading || isPersisting}
              hideSkip={hideSkip}
            />
          </WithRunInBackgroundHelper>
        )}
      </Form>
    </BaseStepLayout>
  )
}
